import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import {
	linkWorkOrderAttachments,
	unLinkWorkOrderAttachments,
} from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import type { WorkOrderAttachment } from "../../../../models/WorkOrderAttachment";
import { AssetAttachmentTable } from "./asset-Attachments-table";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	WorkOrderId: number | null;
	WorkOrderName: string; // Added prop
	Title: string; // Added prop
	AssetName: string; // Added prop
}

export const AssetAttachmentPicker: React.FC<Props> = ({
	show,
	onHide,
	WorkOrderId,
	WorkOrderName,
	Title,
	AssetName,
}) => {
	const {
		state: { assetAttachments, workOrderAttachmentsList },
		actions: {},
	} = useWorkOrderModalsContext();

	const [selectedAttachment, setSelectedAttachment] = useState<
		WorkOrderAttachment[]
	>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (show) {
			setSelectedAttachment(
				workOrderAttachmentsList.filter(
					(WorkOrderAttachment) => WorkOrderAttachment.IsAssetResource,
				),
			);
		}
	}, [show, workOrderAttachmentsList]);

	const handleSelectionChanged = (
		newSelectedAttachment: WorkOrderAttachment[],
	) => {
		setSelectedAttachment(newSelectedAttachment);
	};

	const getNewlyAddedAttachment = (
		currentSelected: WorkOrderAttachment[],
		originalList: WorkOrderAttachment[],
	): WorkOrderAttachment[] => {
		return currentSelected.filter(
			(selectedAttachment) =>
				!originalList.some(
					(existingAttachment) =>
						existingAttachment.UploadFileId ===
							selectedAttachment.UploadFileId &&
						existingAttachment.IsAssetResource,
				),
		);
	};

	const getRemovedAttachment = (
		currentSelected: WorkOrderAttachment[],
		originalList: WorkOrderAttachment[],
	): WorkOrderAttachment[] => {
		return originalList.filter(
			(originalAttachment) =>
				originalAttachment.IsAssetResource &&
				!currentSelected.some(
					(selectedAttachment) =>
						selectedAttachment.UploadFileId === originalAttachment.UploadFileId,
				),
		);
	};

	const handleSave = async () => {
		const newlyAddedAttachments = getNewlyAddedAttachment(
			selectedAttachment,
			workOrderAttachmentsList,
		);
		const removedAttachments = getRemovedAttachment(
			selectedAttachment,
			workOrderAttachmentsList,
		);

		if (newlyAddedAttachments.length > 0) {
			const newAssetAttachments = newlyAddedAttachments.map((attachment) => ({
				UploadFileId: attachment.UploadFileId,
				FileName: attachment.FileName,
				IsAssetResource: true,
			}));
			await linkWorkOrderAttachments(
				WorkOrderId as number,
				newAssetAttachments,
			);
			onHide(true);
		} else onHide(false);

		if (removedAttachments.length > 0) {
			const attachmentsToRemove = removedAttachments.map((attachment) => ({
				UploadFileId: attachment.UploadFileId,
				IsAssetResource: true,
			}));
			await unLinkWorkOrderAttachments(
				WorkOrderId as number,
				attachmentsToRemove,
			);
			onHide(true);
		} else onHide(false);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						ATTACHMENTS FROM ASSET
					</Typography>
					<Typography
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "1.3rem",
							flexWrap: "wrap",
							gap: "8px",
							padding: "8px 0 0",
						}}
					>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Work Order:</span>
							<span style={{ marginLeft: "4px" }}>{WorkOrderName}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Title:</span>
							<span style={{ marginLeft: "4px" }}>{Title}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Asset:</span>
							<span style={{ marginLeft: "4px" }}>{AssetName}</span>
						</div>
					</Typography>
				</DialogTitle>
				<DialogContent>
					<AssetAttachmentTable
						workOrderAttachment={workOrderAttachmentsList.filter(
							(WorkOrderAttachment) => WorkOrderAttachment.IsAssetResource,
						)}
						onSelectionChanged={handleSelectionChanged}
					/>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => onHide(false)}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSave}
						sx={{ minWidth: 120 }}
					>
						SAVE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
